/**
 * A modal window is a graphical control element subordinate to an application&#x27;s main window. It creates a mode that disables the main window but keeps it visible, with the dialog window as a child window in front of it
 *
 * @module views/Layouts/Modal
 */
import React, { forwardRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { isServer } from '../../../utils/device_detection/device_detection';
import './Modal.scss';
import Icon from '../Icon/Icon';
import PropTypes from 'prop-types';
import FocusLock, { InFocusGuard } from 'react-focus-lock'

export const detectDialogState = ( [openMutation] ) => {
  if( openMutation.oldValue === null ){
    // Setting the current scroll posoiton to body
    global.document.body.style.top = `-${global.scrollY}px`;
    global.document.body.classList.add( 'modal-blocked' );
  }
  else {
    const scrollY = global.document.body.style.top;
    global.document.body.classList.remove( 'modal-blocked' );
    global.document.body.style.top = '';
    // Restoring the scroll positon which was set earlier
    global.scrollTo( 0, parseInt( scrollY || '0', 10 ) * -1 );
  }
};

const Modal = forwardRef(
  ( { children, opener, modalAlign, isVideoModal, callBackFunc, videoMeta, closeAccessibilityAction, customClassName }, modalRef ) => {
    const ref = modalRef;

    if( !isServer() && typeof HTMLDialogElement !== 'function' ){
      import( 'dialog-polyfill' ).then( ( { default: { registerDialog } } ) =>
        registerDialog( ref.current )
      );
    }

    useEffect( () => {
      const dialogObserver = new MutationObserver( detectDialogState );
      dialogObserver.observe( ref.current, {
        attributeFilter: ['open'],
        attributeOldValue: true
      } );
      /**
       * creating custom event listener for closing the modal to support in safari browswer
       */

      if( ref.current ){
        ref.current.onclose = ( e ) => {
          opener?.current?.focus();
          if( callBackFunc ){
            callBackFunc();
          }
        };
      }
      document.onclick = ( e ) => {
        // This prevents issues with forms
        if( e.target.tagName !== 'DIALOG' ){
          return;
        }
        const rect = e.target.getBoundingClientRect();

        const clickedInDialog =
          rect.top <= e.clientY &&
          e.clientY <= rect.top + rect.height &&
          rect.left <= e.clientX &&
          e.clientX <= rect.left + rect.width;

        if( clickedInDialog === false ){
          e.target.close();
        }

      };

    }, [] );


    return !isServer() ?
      createPortal(
        <FocusLock group='videoGroup'>
          <div className='Modal__wrapper'
            role='dialog'
            aria-modal='true'
          >
            <dialog
              ref={ ref }
              className={ classNames( `Modal`, {
                ...( modalAlign && { [`Modal--${modalAlign}`]: modalAlign } ),
                ...( isVideoModal && { [`Modal--modalVideo`]: isVideoModal } ),
                ...( customClassName && { [`${customClassName}`]: customClassName } )
              } ) }
              {
                ...( videoMeta ? { style: { width: `${ videoMeta.width }px`, height: `${ videoMeta.height }px` } } : {} )
              }
            >

              <form method='dialog'>
                <button
                  className='Modal__close'
                  aria-label={ closeAccessibilityAction.label }
                  type='submit'

                >
                  <Icon size='s'
                    name={ 'X' }
                  />
                </button>
                <InFocusGuard/>
                { children }
                <InFocusGuard/>
              </form>


            </dialog>
          </div>
        </FocusLock>,
        document.body
      ) :
      null;
  }
);

/**
 * Default values for passed properties
 * @type {object}
 * @property {object} closeAccessibilityAction - Sets close accessibility label and graphql
 */

// TODO: remove default label value when content model is update.

export const defaultProps = {
  closeAccessibilityAction: {
    label: 'close',
    graphql: ''
  }
};

/**
  * Property type definitions
  * @type {object}
  * @property {object} closeAccessibilityAction - Sets close accessibility label and graphql
  */
export const propTypes = {
  /** Sets close accessibility label and graphql */
  closeAccessibilityAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } )
}

Modal.propTypes = propTypes;
Modal.defaultProps = defaultProps;

export default Modal;